import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import React from 'react';
import styled, { css } from 'styled-components';

import { FormTemplatesCategory } from '../../index';
import TEST_ID from './index.testid';
import SelectBlock from '~/components/molecule/SelectBlock';
import { scrollBarStyles } from '~/components/molecule/OverflowScrollWrapper';
import type { FormTemplate } from '~/graphql/types';
import { sort } from 'ramda';
import useTrackEvent from '~/hooks/useTrackEvent';

type Props = RouteComponentProps<{ slug: string }> & {
  formCategories: FormTemplatesCategory;
  onSelect: (selectedTemplate: FormTemplate | null) => void;
  templateToken?: string | null;
};

const TemplateDetails: React.FCC<Props> = ({
  slug,
  formCategories,
  templateToken,
  onSelect,
}) => {
  const trackEvent = useTrackEvent();

  const currentCategory: FormTemplatesCategory['key'] = !slug
    ? formCategories['meest-gebruikt']
    : formCategories[slug];

  const sortedTemplates = currentCategory?.templates
    ? sort(({ name: a, weight: wA = 0 }, { name: b, weight: wB = 0 }) => {
        if (wA === wB) return a.localeCompare(b);
        return wB - wA;
      }, currentCategory.templates)
    : [];

  const handleSelect = (template: FormTemplate) => {
    /** ----- TRACKING: form_selected_template ----- */
    trackEvent({
      eventName: 'form_selected_template',
      parameters: {
        template_id: template.token,
        template_name: template.name,
      },
    });
    onSelect(template);
  };

  return (
    <Container>
      {sortedTemplates.map(template => (
        <SelectBlock
          type="radio"
          key={template.token}
          checked={templateToken === template.token}
          onClick={() => handleSelect(template)}
          description={template.description}
          checkboxTitle={template.name}
          dataTestId={TEST_ID.TEMPLATE}
        />
      ))}
    </Container>
  );
};

const Container = styled.div(
  ({ theme }) => css`
    padding: ${theme.space('s')} ${theme.space('l')};

    /* Magic number 63 to show 4 full entries (most of the time) */
    height: 63vh;
    overform-y: auto;
    width: 100%;

    ${scrollBarStyles}
  `,
);

export default TemplateDetails;
